import { Metadata } from "grpc-web";
import { useCallback, useMemo } from "react";
import { useKeycloak } from "../components/keycloak-provider";
import { useProperty, usePropertyOptional } from "../components/property-provider";
import { IRptToken } from "./auth";

export interface GrpcClientClass<C> {
  new (
    hostname: string,
    credentials?: null | { [index: string]: string },
    options?: null | { [index: string]: string }
  ): C;
}

export type GrpcCall<C, Req, Res> = (this: C, request: Req, metadata?: Metadata) => Promise<Res>;

//Used for debugging grpc locally
//@ts-ignore
const enableDevTools = window.__GRPCWEB_DEVTOOLS__ || (() => {});

export function buildGrpcClient<C>(clientClass: GrpcClientClass<C>): C {
  //If we need to later on change the hostname, we could do it here.
  //Current design is to route everything through the same host, so no need for this.
  const client = new clientClass("");
  enableDevTools([client]);
  return client;
}

/**
 * Creates a new grpc client. The client will not be authenticated.
 * @param clientClass
 */
export function useGrpcClient<C>(clientClass: GrpcClientClass<C>): C {
  return useMemo(() => buildGrpcClient(clientClass), [clientClass]);
}

/**
 * Wraps a GRPC call with a property RPT. This means the call will be authenticated with the current
 * property's RPT.
 * This will error if there is no property RPT available.
 * @param client The client.
 * @param call The call to wrap.
 * @param decodeToken
 */

export function usePropertyGrpcCall<C, Req, Res>(
  client: C,
  call: GrpcCall<C, Req, Res>,
  decodeToken?: IRptToken
): GrpcCall<void, Req, Res> {
  const property = useProperty();
  return useCallback(
    (req: Req, metadata?: Metadata) => {
      const finalMeta = {
        ...(metadata ? metadata : {}),
        "x-principal": decodeToken ? decodeToken.email : "",
        Authorization: `Bearer ${property.token}`,
      };
      return call.apply(client, [req, finalMeta]);
    },
    [decodeToken, property.token, call, client]
  );
}

export function usePropertyOptionalGrpcCall<C, Req, Res>(
  client: C,
  call: GrpcCall<C, Req, Res>
): GrpcCall<void, Req, Res> | undefined {
  const property = usePropertyOptional();

  //We can't put this in an if because hook call cannot be conditional. However, it can go unused.
  const result = useCallback(
    (req: Req, metadata?: Metadata) => {
      const finalMeta = {
        ...(metadata ? metadata : {}),
        Authorization: `Bearer ${property!.token}`,
      };
      return call.apply(client, [req, finalMeta]);
    },
    [property, client, call]
  );

  return property ? result : undefined;
}

/**
 * Wraps a GRPC call with the user's access token. This means the call will be authenticated with
 * the current user's token.
 *
 * This will error if the user is not authenticated.
 * @param client the client.
 * @param call The call to wrap.
 */
export function useUserGrpcCall<Req, Res, C>(
  client: C,
  call: GrpcCall<C, Req, Res>
): GrpcCall<void, Req, Res> {
  const keycloak = useKeycloak();

  return useCallback(
    (req: Req, metadata?: Metadata) => {
      const finalMeta = {
        ...(metadata ? metadata : {}),
        Authorization: `Bearer ${keycloak.token}`,
      };

      return call.apply(client, [req, finalMeta]);
    },
    [keycloak.token, client, call]
  );
}
